import { Body, Callout, TextLink, Tooltip } from '@assembly-js/design-system';
import { FormControl } from '@mui/material';
import { useFormikContext } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';
import { makeStyles } from 'tss-react/mui';

import { FormBodyAPI, PAYMENTS_PAGE } from 'src/constants';
import { getACHDisabledTooltipHostProps } from 'src/features/payments/components/ACHDisabledTooltip';
import { useHasACHCapability } from 'src/features/payments/hooks/useHasACHCapability';
import { SuggestIconPicker } from 'src/features/SuggestIconPicker';
import { useDefaultCurrency } from 'src/hooks/useDefaultCurrency';
import { useAppSelector } from 'src/hooks/useStore';
import { AppVisibilityConfigSetting } from 'src/legacy/components/app-visibility/AppVisibilityConfigSetting';
import { RemoveApp } from 'src/legacy/components/AppSetup/RemoveApp';
import { FormLabel } from 'src/legacy/components/Forms/FormLabel';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import {
  BaseTextField,
  ResizableTextField,
} from 'src/legacy/components/TextField';
import { GraySmall, red, WebLink, white } from 'src/theme/colors';
import { RegularCardShadow } from 'src/theme/shadows';
import { VisibilityScenario } from 'src/utils/app_visibility';

import { SettingsPageRow } from '../SettingsPageRow';
import SettingsToggleCard from '../SettingsToggleCard';

import { validationScheme } from './useBankingSettingsFormValidation';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
  },
  helperText: {
    color: GraySmall,
  },
  iconPickerWrapper: {
    marginTop: theme.spacing(3.5),
    marginBottom: theme.spacing(3.5),
  },
  callout: {
    alignItems: 'center',
    background: white,
    marginBottom: theme.spacing(4),
    boxShadow: RegularCardShadow,
  },
  calloutIcon: {
    fontSize: 16,
    marginRight: theme.spacing(1.5),
  },
  calloutLink: {
    color: WebLink,
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

/**
 * Forces the `allowPaymentViaACH` billing default off whenever the connected
 * account can't accept ACH. Kept as a child component so `shouldBlock` is a
 * real, reactive effect dependency (it can flip to true after `accountInfo`
 * loads).
 */
const ACHBillingDefaultEnforcer: React.FC<{ shouldBlock: boolean }> = ({
  shouldBlock,
}) => {
  const { values, setFieldValue } = useFormikContext<{
    allowPaymentViaACH?: boolean;
  }>();

  React.useEffect(() => {
    if (shouldBlock && values.allowPaymentViaACH) {
      void setFieldValue('allowPaymentViaACH', false);
    }
  }, [shouldBlock, values.allowPaymentViaACH, setFieldValue]);

  return null;
};

export const useBillingSettingsForm = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const defaultCurrency = useDefaultCurrency();
  const { shouldBlockBankPayments } = useHasACHCapability();
  const accountInfo = useAppSelector(
    (state) => state.payments.accountInfo,
    shallowEqual,
  );

  const FormRenderer: React.FC<FormBodyAPI> = ({
    errors,
    values,
    handleBlur,
    handleChange,
    touched,
    setFieldValue,
  }) => {
    const filteredBillingDefaultsOptions = React.useMemo(
      () =>
        [
          {
            fieldName: 'allowPaymentViaACH',
            title: t(
              ($) =>
                $.page_billing.billing_defaults.options.allow_payment_via_ach
                  .title,
            ),
            description: t(
              ($) =>
                $.page_billing.billing_defaults.options.allow_payment_via_ach
                  .description,
            ),
          },
          {
            fieldName: 'absorbACHFees',
            title: t(
              ($) =>
                $.page_billing.billing_defaults.options.absorb_ach_fees.title,
            ),
            description: t(
              ($) =>
                $.page_billing.billing_defaults.options.absorb_ach_fees
                  .description,
            ),
          },
          {
            fieldName: 'allowPaymentViaCC',
            title: t(
              ($) =>
                $.page_billing.billing_defaults.options.allow_payment_via_cc
                  .title,
            ),
            description: t(
              ($) =>
                $.page_billing.billing_defaults.options.allow_payment_via_cc
                  .description,
            ),
          },
          {
            fieldName: 'absorbCreditCardFee',
            title: t(
              ($) =>
                $.page_billing.billing_defaults.options.absorb_credit_card_fee
                  .title,
            ),
            description: t(
              ($) =>
                $.page_billing.billing_defaults.options.absorb_credit_card_fee
                  .description,
            ),
          },
        ].filter((item) => {
          // do not show ACH payment setting for non-usd currencies
          if (
            (item.fieldName === 'allowPaymentViaACH' ||
              item.fieldName === 'absorbACHFees') &&
            defaultCurrency.toUpperCase() !== 'USD'
          ) {
            return false;
          }

          // if ACH payment is disabled, do not show ACH fee setting
          if (
            item.fieldName === 'absorbACHFees' &&
            !values.allowPaymentViaACH
          ) {
            return false;
          }

          // if credit card payment is disabled, do not show credit card fee setting
          if (
            item.fieldName === 'absorbCreditCardFee' &&
            !values.allowPaymentViaCC
          ) {
            return false;
          }

          return true;
        }),
      [values.allowPaymentViaACH, values.allowPaymentViaCC],
    );

    return (
      <>
        <ACHBillingDefaultEnforcer shouldBlock={shouldBlockBankPayments} />
        {/* if we have bank accont we don't want to render this callout */}
        {!accountInfo && (
          <div className="mb-10">
            <Callout
              description={
                <>
                  To receive test payouts, you’ll need to&nbsp;
                  <TextLink href={PAYMENTS_PAGE.path} className="inline-flex">
                    set up a payout account
                  </TextLink>
                  &nbsp;first.
                </>
              }
            />
          </div>
        )}
        <Tooltip
          position="bottom"
          content={
            <Body size="sm" className="text-secondary">
              Editing the name is disabled for this module.
            </Body>
          }
          disabled={true}
        >
          <div>
            <BaseTextField
              autoFocus
              fullWidth
              label={
                <div className={classes.container}>
                  <BaseTypography fontType="13Medium">Name</BaseTypography>
                  <BaseTypography className={classes.helperText}>
                    &nbsp;- How it should appear in the sidebar
                  </BaseTypography>
                </div>
              }
              variant="outlined"
              sizeVariant="medium"
              name="label"
              value={values.label}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(
                touched && touched.label && errors && errors.label,
              )}
              helperText={touched && touched.label && errors && errors.label}
              disabled={false}
            />
          </div>
        </Tooltip>
        <div className={classes.iconPickerWrapper}>
          <BaseTypography fontType="13Medium">Sidebar icon</BaseTypography>
          <SuggestIconPicker
            value={values.icon}
            onChange={(iconName) => setFieldValue('icon', iconName)}
            appName={values.label}
          />
        </div>
        <SettingsPageRow hideDivider>
          <BaseTypography fontType="18Medium">
            {t(($) => $.page_billing.billing_defaults.title)}
          </BaseTypography>
          <BaseTypography className={classes.helperText}>
            {t(($) => $.page_billing.billing_defaults.description)}
          </BaseTypography>
        </SettingsPageRow>
        <RowDivider mb={3} />
        <div className="w-full relative">
          {filteredBillingDefaultsOptions.map((option) => {
            const isACHOption = option.fieldName === 'allowPaymentViaACH';
            // ACH is forced off (above) when the capability is missing, so the
            // toggle is simply disabled with an explanatory tooltip.
            const disableForMissingCapability =
              isACHOption && shouldBlockBankPayments;
            return (
              <SettingsToggleCard
                titleFontType={'13Medium'}
                key={option.fieldName}
                checked={!!values[option.fieldName]}
                fieldName={option.fieldName}
                title={option.title}
                description={option.description}
                disabled={disableForMissingCapability}
                {...(disableForMissingCapability
                  ? getACHDisabledTooltipHostProps()
                  : {})}
                useDesignSystemToggle
              />
            );
          })}
          {errors?.allowPaymentViaCC && (
            <BaseTypography fontType="13Medium" style={{ color: red }}>
              {errors.allowPaymentViaCC}
            </BaseTypography>
          )}

          <div className="my-8">
            <FormControl fullWidth>
              <FormLabel
                primaryText="Default memo"
                secondaryText=' - This will prefill the memo field in the "create an invoice/subscription" flow'
              />

              <ResizableTextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                name="defaultMemo"
                value={values.defaultMemo}
                error={Boolean(errors && errors.defaultMemo)}
                helperText={errors && errors.defaultMemo}
              />
            </FormControl>
          </div>
        </div>
        <AppVisibilityConfigSetting scenario={VisibilityScenario.EditApp} />
        <RemoveApp />
      </>
    );
  };

  return {
    validationScheme,
    FormRenderer,
  };
};
